import { Grid, Typography, alpha } from "@mui/material";
import { Box, Container, styled } from "@mui/system";
import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useTenantConfig } from "../hooks/useTenant";

const S = { main: '#0891b2', light: '#22d3ee', dark: '#0e7490' };

/* Section badge (pill) */
const SectionBadge = styled(Box)({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    borderRadius: 50,
    background: 'rgba(8, 145, 178, 0.08)',
    border: '1px solid rgba(8, 145, 178, 0.15)',
    marginBottom: 20,
});



const fadeUp = {
    hidden: { opacity: 0, y: 36 },
    visible: (i = 0) => ({
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.65,
            ease: [0.22, 1, 0.36, 1],
            delay: i * 0.1,
        },
    }),
};

const OfficialInfrastructureSection = ({ isMobile }) => {
    const { tenantName } = useTenantConfig();

    const ref = useRef(null);
    const isInView = useInView(ref, { once: true, margin: '-80px' });

    const FEATURES = [
        {
            image: '/assets/icon/contract.webp',
            title: 'E-İmza Destekli Belge Yönetimi',
            desc: 'Resmi belgelerinizi elektronik imza ile güvenli ve doğrulanabilir şekilde oluşturun.',
        },
        {
            image: '/assets/icon/security.webp',
            title: 'Mevzuata Uygun EYP Üretimi',
            desc: 'Resmi yazışma mevzuatına uygun Elektronik Yazışma Paketi (EYP) üretin.',
        },
        {
            image: '/assets/icon/email.webp',
            title: 'Entegre KEP Sistemi',
            desc: 'Kayıtlı Elektronik Posta (KEP) entegrasyonu ile kamu kurumlarıyla doğrudan iletişim kurun.',
        },
        {
            image: '/assets/icon/folder.webp',
            title: 'Güvenli ve Denetlenebilir Arşiv',
            desc: 'Tüm resmi belgeler kayıt altına alınır, arşivlenir ve denetime hazır tutulur.',
        },
    ];

    return (
        <Box
            ref={ref}
            sx={{
                py: { xs: 8, md: 14 },
                background: '#f8fafc',
                borderTop: '1px solid #f1f5f9',
            }}
        >
            <Container maxWidth="lg">
                <Grid container spacing={6} alignItems="center">

                    {/* SOL */}
                    <Grid item xs={12} md={5}>
                        <motion.div
                        >
                            <SectionBadge>
                                <Box
                                    sx={{
                                        width: 8,
                                        height: 8,
                                        borderRadius: '50%',
                                        background: S.main,
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: '0.8rem',
                                    fontWeight: 600,
                                    color: S.main,
                                    letterSpacing: '0.5px',
                                }}>
                                    SADECE WEB SİTESİ DEĞİL
                                </Typography>
                            </SectionBadge>

                            <Typography
                                variant="h3"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: { xs: '2.4rem', md: '3rem' },
                                    color: '#334155',
                                    lineHeight: 1.3,
                                    letterSpacing: '-0.01em',
                                    mb: 2.5,
                                }}
                            >
                                Resmi Altyapılı {' '}
                                <Box
                                    component="span"
                                    sx={{
                                        color: S.main,
                                        fontWeight: 700,
                                    }}
                                >
                                    Dijital Dernek Yönetimi
                                </Box>
                            </Typography>

                            <Typography
                                sx={{
                                    color: '#64748b',
                                    fontSize: '1rem',
                                    lineHeight: 1.75,
                                    mb: 4,
                                }}
                            >
                                {tenantName}, dernek web sitesi oluşturmanın ötesinde,
                                kamu kurumlarıyla uyumlu resmi yazışma süreçlerini destekleyen
                                e-imza tabanlı bir dijital yönetim platformudur.
                            </Typography>

                            <Box
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2,
                                    background: alpha(S.main, 0.06),
                                    border: `1px solid ${alpha(S.main, 0.12)}`,
                                }}
                            >
                                <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#0f172a' }}>
                                    Turkcell bulut altyapısı üzerinde çalışan,
                                    SLA destekli ve denetime hazır platform.
                                </Typography>
                            </Box>
                        </motion.div>
                    </Grid>

                    {/* SAĞ */}
                    <Grid item xs={12} md={7}>
                        <motion.div
                        >
                            <Grid container spacing={2}>
                                {FEATURES.map((f, i) => {
                                    return (
                                        <Grid item xs={12} sm={6} key={i}>
                                            <motion.div
                                                variants={fadeUp}
                                                custom={i + 1}
                                                initial="hidden"
                                                animate={isInView ? 'visible' : 'hidden'}
                                                style={{ height: '100%' }}
                                            >
                                                <Box
                                                    sx={{
                                                        p: 3,
                                                        borderRadius: 3,
                                                        border: '1px solid rgba(0,0,0,0.06)',
                                                        background: '#ffffff',
                                                        height: '100%',
                                                        transition: 'all 0.25s ease',
                                                        '&:hover': {
                                                            transform: 'translateY(-4px)',
                                                            boxShadow: '0 12px 24px rgba(0,0,0,0.06)',
                                                        },
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            width: 52,
                                                            height: 52,
                                                            borderRadius: 2.5,
                                                            background: `linear-gradient(135deg, ${S.main} 0%, ${S.light} 100%)`,
                                                            boxShadow: `0 8px 20px ${alpha(S.main, 0.25)}`,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            mb: 2.5,
                                                        }}
                                                    >
                                                        <img
                                                            src={f.image}
                                                            alt={f.title}
                                                            style={{
                                                                width: '60%',
                                                                height: '60%',
                                                                filter: 'brightness(0) invert(1)'
                                                            }}
                                                        />
                                                    </Box>

                                                    <Typography
                                                        sx={{
                                                            fontWeight: 700,
                                                            fontSize: '0.95rem',
                                                            color: '#0f172a',
                                                            mb: 1,
                                                        }}
                                                    >
                                                        {f.title}
                                                    </Typography>

                                                    <Typography
                                                        sx={{
                                                            fontSize: '0.85rem',
                                                            color: '#64748b',
                                                            lineHeight: 1.6,
                                                        }}
                                                    >
                                                        {f.desc}
                                                    </Typography>
                                                </Box>
                                            </motion.div>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </motion.div>
                    </Grid>

                </Grid>
            </Container>
        </Box>
    );
};

export default OfficialInfrastructureSection;